/**
 * Chatbot Messages & Chat Logic
 * Handles message display, chat API interaction, and UI updates
 */

function initChatbotMessages() {
  'use strict';

  // State management
  let isSending = false;
  // Token to discard stale API responses when user switches products quickly.
  // Any UI state change that should "invalidate" in-flight requests bumps this.
  let uiConversationToken = 0;
  let messagesContainer;
  let input;
  let sendBtn;
  let CUSTOMER_ID;
  let CUSTOMER_EMAIL;
  let CUSTOMER_NAME;
  let API_ENDPOINT;
  let typingIndicatorElement = null;
  let activeGuidedOptions = null; // Store options for persistent guided chat
  let askedQuestions = new Set(); // Track questions the user already asked
  let askedQuestionsByProduct = {}; // Stable asked-state cache per product key
  let productHistories = {};      // PERSISTENCE: Store message history per product
  let currentProductContext = null; // Product user clicked for guided chat (product_id, product_name for API)
  let currentGuidedProductKey = null;
  let guidedRepliesTimeout = null;
  const CUSTOM_QUESTION_OPTION = 'my question is not listed above';
  const GUIDED_STATE_STORAGE_KEY = 'codeskin_chatbot_guided_state_v1';

  function normalizeGuidedQuestion(text) {
    return String(text || '')
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function getGuidedProductKey(ctx) {
    if (!ctx) return '';
    // Cache key must stay stable across different product object shapes:
    // - numeric Shopify product id
    // - or handle-like id when numeric id is missing
    // - variant id when the product has multiple usages (AM/PM, etc.)
    const pid = String(ctx.cache_product_id || ctx.product_id || '').trim();
    const vid = String(ctx.variant_id || '').trim();
    if (pid || vid) return [pid, vid].join('|');
    // Final fallback (only if both pid/vid are empty).
    const pname = normalizeGuidedQuestion(ctx.product_name || '');
    return ['pname', pname].join('|');
  }

  function persistAskedQuestionsForCurrentProduct() {
    if (!currentGuidedProductKey) return;
    askedQuestionsByProduct[currentGuidedProductKey] = new Set(Array.from(askedQuestions));
    flushGuidedStateToStorage();
  }

  function flushGuidedStateToStorage() {
    try {
      var histories = {};
      Object.keys(productHistories).forEach(function (id) {
        var h = productHistories[id];
        if (!h) return;
        histories[id] = {
          html: String(h.html || ''),
          asked: Array.from(h.asked || []).map(normalizeGuidedQuestion),
          options: Array.isArray(h.options) ? h.options : []
        };
      });
      var askedByKey = {};
      Object.keys(askedQuestionsByProduct).forEach(function (k) {
        var s = askedQuestionsByProduct[k];
        askedByKey[k] = Array.from(s || []).map(normalizeGuidedQuestion);
      });
      localStorage.setItem(GUIDED_STATE_STORAGE_KEY, JSON.stringify({
        v: 1,
        histories: histories,
        askedByKey: askedByKey
      }));
    } catch (e) {
      /* quota / private mode */
    }
  }

  function loadGuidedStateFromStorage() {
    try {
      var raw = localStorage.getItem(GUIDED_STATE_STORAGE_KEY);
      if (!raw) return;
      var data = JSON.parse(raw);
      if (!data || data.v !== 1) return;
      if (data.histories && typeof data.histories === 'object') {
        Object.keys(data.histories).forEach(function (id) {
          var h = data.histories[id];
          if (!h) return;
          productHistories[id] = {
            html: String(h.html || ''),
            asked: new Set(Array.from(h.asked || []).map(normalizeGuidedQuestion)),
            options: Array.isArray(h.options) ? h.options : []
          };
        });
      }
      if (data.askedByKey && typeof data.askedByKey === 'object') {
        Object.keys(data.askedByKey).forEach(function (k) {
          askedQuestionsByProduct[k] = new Set(Array.from(data.askedByKey[k] || []).map(normalizeGuidedQuestion));
        });
      }
    } catch (e) {
      /* ignore corrupt storage */
    }
  }

  /**
   * Backend requires shopify_product_id on guided product chat. After loadHistory / reopen,
   * we may have currentGuidedProductKey set but currentProductContext missing or empty.
   * Keys from chatbot-main look like: "123456789", "123456789_v_VARIANT", "123456789_t_am".
   */
  function applyProductContextFromGuidedKey(guidedKey) {
    if (!guidedKey) return;
    var s = String(guidedKey);
    if (s.indexOf('_n_') === 0) return;
    var m = s.match(/^(\d+)(?:_v_(.+)|_t_(.+))?$/);
    if (!m || !m[1]) return;
    var pid = m[1];
    var variantId = m[2] ? String(m[2]).trim() : null;
    var prevName = currentProductContext && currentProductContext.product_name ? currentProductContext.product_name : null;
    if (!currentProductContext) {
      currentProductContext = {
        product_id: pid,
        variant_id: variantId,
        product_name: prevName
      };
      return;
    }
    if (!String(currentProductContext.product_id || '').trim()) {
      currentProductContext.product_id = pid;
    }
    if (variantId && m[2]) {
      currentProductContext.variant_id = variantId;
    }
    if (prevName && !currentProductContext.product_name) {
      currentProductContext.product_name = prevName;
    }
  }

  /** Save current messages + asked chips for the active guided product (same key as setGuidedProductKey). */
  function persistCurrentProductSnapshot() {
    if (!messagesContainer || !currentGuidedProductKey) return;
    productHistories[currentGuidedProductKey] = {
      html: messagesContainer.innerHTML,
      asked: new Set(Array.from(askedQuestions).map(normalizeGuidedQuestion)),
      options: Array.isArray(activeGuidedOptions) ? activeGuidedOptions : []
    };
    askedQuestionsByProduct[currentGuidedProductKey] = new Set(Array.from(askedQuestions));
    flushGuidedStateToStorage();
    console.log("[ChatbotMessages] Snapshot persisted for", currentGuidedProductKey, "with", (activeGuidedOptions ? activeGuidedOptions.length : 0), "options");
  }

  function setGuidedProductKey(guidedKey) {
    if (!guidedKey) {
      currentGuidedProductKey = null;
      askedQuestions.clear();
      // Do not clear activeGuidedOptions here; it's managed by setGuidedOptions
      bumpConversationToken();
      return;
    }
    persistAskedQuestionsForCurrentProduct();
    currentGuidedProductKey = String(guidedKey);
    const cachedAsked = askedQuestionsByProduct[currentGuidedProductKey];
    askedQuestions = new Set(Array.from(cachedAsked || []).map(normalizeGuidedQuestion));
    applyProductContextFromGuidedKey(currentGuidedProductKey);
    bumpConversationToken();
  }

  function bumpConversationToken() {
    uiConversationToken += 1;
  }

  // Cancel current request/typing state so switching products never leaks stale loading/responses.
  function cancelPendingResponse() {
    bumpConversationToken();
    if (guidedRepliesTimeout) {
      clearTimeout(guidedRepliesTimeout);
      guidedRepliesTimeout = null;
    }
    hideTypingIndicator();
    isSending = false;
    if (input) input.disabled = false;
    if (sendBtn) sendBtn.disabled = false;
  }

  /**
   * Set DOM references after elements are created
   */
  function setDOMReferences(refs) {
    messagesContainer = refs.messagesContainer;
    input = refs.input;
    sendBtn = refs.sendBtn;
    CUSTOMER_ID = refs.CUSTOMER_ID;
    CUSTOMER_EMAIL = refs.CUSTOMER_EMAIL;
    CUSTOMER_NAME = refs.CUSTOMER_NAME;
    API_ENDPOINT = refs.API_ENDPOINT;

    if (messagesContainer && !messagesContainer._quickReplyDelegated) {
      messagesContainer._quickReplyDelegated = true;
      messagesContainer.addEventListener('click', function (e) {
        const btn = e.target.closest('.quick-reply-btn');
        if (!btn || btn.disabled) return;
        const opt = btn.textContent.trim();
        const normalizedOpt = normalizeGuidedQuestion(opt);
        if (askedQuestions.has(normalizedOpt) && normalizedOpt !== CUSTOM_QUESTION_OPTION) {
          btn.disabled = true;
          btn.classList.add('already-asked-btn');
          return;
        }
        if (normalizedOpt === CUSTOM_QUESTION_OPTION) {
          const inputContainer = document.getElementById('chatbot-input');
          if (inputContainer) inputContainer.style.display = 'flex';
          focusInputSafely();
          if (input && input.placeholder) {
            const orig = input.placeholder;
            input.placeholder = 'Type your question here...';
            setTimeout(function () { input.placeholder = orig; }, 3000);
          }
        } else {
          if (isSending) return;
          // Remove current FAQ chips immediately so the next Q/A pair appears together.
          // Chips are re-added after bot response (or error) in sendMessage().
          const existingReplies = messagesContainer.querySelectorAll('.quick-replies-container');
          existingReplies.forEach(function (node) {
            try { node.remove(); } catch (eRemove) { /* ignore */ }
          });
          askedQuestions.add(normalizedOpt);
          persistAskedQuestionsForCurrentProduct();
          if (input) input.value = opt;
          if (sendBtn) sendBtn.click();
        }
      });
    }
  }

  // Debounce scroll operations for performance
  let scrollTimeout = null;

  /**
   * Scroll messages container to top (for guided chat start)
   */
  function scrollToTop(smooth = false) {
    if (scrollTimeout) clearTimeout(scrollTimeout);
    scrollTimeout = requestAnimationFrame(() => {
      if (messagesContainer) {
        if (smooth) {
          messagesContainer.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          messagesContainer.scrollTop = 0;
        }
      }
      scrollTimeout = null;
    });
  }

  /**
   * Scroll messages container to bottom (optimized)
   */
  function scrollToBottom(smooth = false) {
    // Clear any pending scroll
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }

    // Use requestAnimationFrame for smooth scrolling
    scrollTimeout = requestAnimationFrame(() => {
      if (messagesContainer) {
        if (smooth) {
          messagesContainer.scrollTo({
            top: messagesContainer.scrollHeight,
            behavior: 'smooth'
          });
        } else {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      }
      scrollTimeout = null;
    });
  }

  function focusInputSafely() {
    if (!input) return;
    try {
      input.focus({ preventScroll: true });
    } catch (e) {
      input.focus();
    }
  }

  function scrollMessageIntoViewInsideMessages(messageElement, smooth = true) {
    if (!messagesContainer || !messageElement) return;
    var targetTop = Math.max(0, messageElement.offsetTop - 8);
    if (smooth && typeof messagesContainer.scrollTo === 'function') {
      messagesContainer.scrollTo({ top: targetTop, behavior: 'smooth' });
    } else {
      messagesContainer.scrollTop = targetTop;
    }
  }

  /**
   * Create a message DOM element
   */
  function createMessage(text, isUser, isQuickReplies = false) {
    const messageDiv = document.createElement("div");
    messageDiv.className = isUser ? 'message user-message' : 'message bot-message';
    const isFaqIntro = !isUser && /^Everything you'd want to know about\b/i.test(String(text || '').trim());
    if (isFaqIntro) {
      messageDiv.classList.add('faq-intro-message');
    }

    if (!isUser) {
      const avatar = document.createElement("div");
      avatar.className = "message-avatar";
      const iconUrl = String(
        window.CODESKIN_LOGO_URL ||
        ((typeof window.ChatbotConfig !== 'undefined' && window.ChatbotConfig.images && window.ChatbotConfig.images.avatar)
          ? window.ChatbotConfig.images.avatar
          : '') ||
        ''
      ).trim();
      if (iconUrl) {
         const img = document.createElement("img");
         img.src = iconUrl;
         img.alt = "Codi";
         img.className = "chatbot-bot-avatar-img";
         avatar.appendChild(img);
      } else {
         // No logo URL configured: use Codi image asset (never show robot SVG)
         const fallbackImg = document.createElement("img");
         fallbackImg.src = (window.CODESKIN_LOGO_URL || window.CHATBOT_LOGO_FALLBACK_URL || '').trim()
           || '/cdn/shop/files/codeskin_logo_image.png';
         fallbackImg.alt = "Codi";
         fallbackImg.className = "chatbot-bot-avatar-img";
         fallbackImg.onerror = function() {
           this.style.display = 'none';
           this.parentElement.style.background = 'rgba(239,126,25,1)';
           this.parentElement.innerHTML = '<span style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:14px;">C</span>';
         };
         avatar.appendChild(fallbackImg);
      }
      if (isFaqIntro) avatar.classList.add('faq-intro-avatar');
      messageDiv.appendChild(avatar);
    }

    const content = document.createElement("div");
    content.className = "message-content";
    if (isUser) {
      content.textContent = text;
    } else {
      content.innerHTML = formatMessage(text);
    }

    messageDiv.appendChild(content);

    return messageDiv;
  }

  /**
   * Simple Markdown-like formatter
   */
  function formatMessage(text) {
    if (!text) return "";

    // SECURITY: Escape all HTML from the API so bot responses can't inject scripts.
    // We still render a controlled subset of formatting tags produced by our own code.
    const escapeHtml = function (s) {
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    };

    text = escapeHtml(text);

    // First, handle bold headers
    let sections = text.split(/(\*\*.*?\*\*)/);

    let formatted = sections.map(part => {
      if (!part) return "";

      if (part.startsWith('**') && part.endsWith('**')) {
        return `<h4>${part.replace(/\*\*/g, '')}</h4>`;
      }

      // Handle numbered lists (both multiline and inline)
      // Look for patterns like "1. ItemName"
      let processed = part
        .replace(/(\d+\.\s+)(.*?)(?=\s+\d+\.\s+|\n|$)/g, (match, num, content) => {
          return `<li>${content.trim()}</li>`;
        });

      // If we found list items, wrap them in an <ol>
      if (processed.includes('<li>')) {
        // Clean up extra line breaks around list
        return `<ol>${processed.replace(/<br\s*\/?>/gi, '')}</ol>`;
      }

      // Handle remaining line breaks
      return processed.replace(/\n/g, '<br/>');
    }).join('');

    // Final cleanup of empty tags or double breaks
    return formatted
      .replace(/<ol>\s*<\/ol>/g, '')
      .replace(/(<br\/>){3,}/g, '<br/><br/>');
  }

  /**
   * Show typing indicator
   */
  function showTypingIndicator() {
    if (typingIndicatorElement) return;

    const messageDiv = document.createElement("div");
    messageDiv.className = 'message bot-message typing-message';
    messageDiv.id = 'chatbot-typing-indicator';

    const avatar = document.createElement("div");
    avatar.className = "message-avatar";
    const iconUrl = String(
      window.CODESKIN_LOGO_URL ||
      ((typeof window.ChatbotConfig !== 'undefined' && window.ChatbotConfig.images && window.ChatbotConfig.images.avatar)
        ? window.ChatbotConfig.images.avatar
        : '') ||
      ''
    ).trim();
    if (iconUrl) {
        const img = document.createElement("img");
        img.src = iconUrl;
        img.alt = "Codi";
        img.className = "chatbot-bot-avatar-img";
        avatar.appendChild(img);
    } else {
        const fallbackImg = document.createElement("img");
        fallbackImg.src = (window.CODESKIN_LOGO_URL || window.CHATBOT_LOGO_FALLBACK_URL || '').trim()
          || '/cdn/shop/files/codeskin_logo_image.png';
        fallbackImg.alt = "Codi";
        fallbackImg.className = "chatbot-bot-avatar-img";
        fallbackImg.onerror = function() {
          this.style.display = 'none';
          this.parentElement.style.background = 'rgba(239,126,25,1)';
          this.parentElement.innerHTML = '<span style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:14px;">C</span>';
        };
        avatar.appendChild(fallbackImg);
    }
    messageDiv.appendChild(avatar);

    const indicator = document.createElement("div");
    indicator.className = "typing-indicator";
    indicator.innerHTML = `
      <div class="typing-dot"></div>
      <div class="typing-dot"></div>
      <div class="typing-dot"></div>
    `;

    messageDiv.appendChild(indicator);
    messagesContainer.appendChild(messageDiv);
    typingIndicatorElement = messageDiv;

    scrollToBottom(true);
  }

  /**
   * Hide typing indicator
   */
  function hideTypingIndicator() {
    if (typingIndicatorElement) {
      typingIndicatorElement.remove();
      typingIndicatorElement = null;
    }
  }

  /**
   * Add a message to the chat
   */
  function addMessage(text, isUser, shouldScroll = true) {
    const messageElement = createMessage(text, isUser);
    if (!isUser && /^Everything you'd want to know about\b/i.test(String(text || '').trim())) {
      messageElement.classList.add('faq-intro-message');
      let avatar = messageElement.querySelector('.message-avatar');
      if (!avatar) {
        avatar = document.createElement('div');
        avatar.className = 'message-avatar faq-intro-avatar';
        messageElement.insertBefore(avatar, messageElement.firstChild);
      } else if (!avatar.classList.contains('faq-intro-avatar')) {
        avatar.classList.add('faq-intro-avatar');
      }
      if (!avatar.querySelector('img')) {
        const iconUrl = String(
          window.CODESKIN_LOGO_URL ||
          ((typeof window.ChatbotConfig !== 'undefined' && window.ChatbotConfig.images && window.ChatbotConfig.images.avatar)
            ? window.ChatbotConfig.images.avatar
            : '') ||
          ''
        ).trim();
        if (iconUrl) {
          avatar.innerHTML = '';
          const img = document.createElement('img');
          img.src = iconUrl;
          img.alt = 'Codi';
          img.className = 'chatbot-bot-avatar-img';
          avatar.appendChild(img);
        }
      }
    }
    messagesContainer.appendChild(messageElement);

    if (shouldScroll) {
      setTimeout(function () {
        if (isUser) {
          // Standard scroll for user: see what you just typed
          scrollToBottom(true);
        } else {
          // Keep answer scrolling inside chatbot messages pane only.
          scrollMessageIntoViewInsideMessages(messageElement, true);
          
          // FORCED RECOVERY: If we are in guided mode, ensure replies are re-added
          // directly after the bot message is added.
          if (activeGuidedOptions && Array.isArray(activeGuidedOptions) && activeGuidedOptions.length > 0) {
            ensureGuidedRepliesVisible();
          }
        }
      }, 100);
    }
  }

  /**
   * Get or create a unique guest ID for anonymous users (numeric only)
   */
  function getGuestId() {
    try {
      let guestId = localStorage.getItem('chatbot_guest_id');
      // If existing ID is non-numeric or missing, create a new numeric one
      if (!guestId || isNaN(guestId.replace('guest_', ''))) {
        guestId = Date.now().toString() + Math.floor(Math.random() * 1000).toString();
        localStorage.setItem('chatbot_guest_id', guestId);
      }
      // Return numeric part only
      return guestId.replace('guest_', '');
    } catch (e) {
      return Date.now().toString();
    }
  }

  async function sendMessageToAPI(message, questionnaireAnswers = []) {
    // Determine if user is logged in or guest
    const isGuest = !CUSTOMER_ID || CUSTOMER_ID === null || CUSTOMER_ID === 'null' || CUSTOMER_ID === '';

    // Use numeric ID
    const rawId = isGuest ? getGuestId() : CUSTOMER_ID.toString();
    const userShopifyId = rawId.replace('guest_', '');

    // For guest users, use "Guest User" as name, otherwise use actual name
    const userName = (isGuest || !CUSTOMER_NAME || CUSTOMER_NAME.trim() === "")
      ? "Guest User"
      : CUSTOMER_NAME.trim();

    // For guest users, use empty string for email, otherwise use actual email
    const userEmail = (isGuest || !CUSTOMER_EMAIL || CUSTOMER_EMAIL.trim() === "")
      ? ""
      : CUSTOMER_EMAIL.trim();

    // Guided chat: API requires shopify_product_id — hydrate from key if context was lost (e.g. after loadHistory).
    if (currentGuidedProductKey && (!currentProductContext || !String(currentProductContext.product_id || '').trim())) {
      applyProductContextFromGuidedKey(currentGuidedProductKey);
    }

    // API schema: message, user_shopify_id, user_name, user_email + product context when in guided chat
    const payload = {
      message: String(message),
      user_shopify_id: String(userShopifyId),
      user_name: String(userName),
      user_email: String(userEmail)
    };
    if (currentProductContext && String(currentProductContext.product_id || '').trim()) {
      payload.shopify_product_id = String(currentProductContext.product_id);
      if (currentProductContext.variant_id) payload.shopify_variant_id = String(currentProductContext.variant_id);
      if (currentProductContext.product_name) payload.product_name = String(currentProductContext.product_name);
    }


    try {
      // Use optimized API utility with rate limiting (fallback to standard fetch if not available)
      let data;
      if (window.ChatbotAPI && typeof window.ChatbotAPI.post === 'function') {
        data = await window.ChatbotAPI.post(API_ENDPOINT, payload, {
          requireRateLimit: true,
          timeout: 30000, // 30s for chat – LLM/backend often needs 15–25s
          retries: 2 // Retry once on timeout/slow response
        });
      } else {
        // Fallback to standard fetch
        // SECURITY/RESILIENCE: add timeout to avoid hanging UI
        const controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
        const timeoutId = controller ? setTimeout(function () { controller.abort(); }, 30000) : null;
        const response = await fetch(API_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify(payload),
          signal: controller ? controller.signal : undefined
        });
        if (timeoutId) clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        data = await response.json();
      }

      // 1. Deep Error Detection: Check for error flags at top level OR inside conversation_state
      const conversationState = data.conversation_state || {};
      const hasError = data.status === 'error' ||
        data.has_error === true ||
        data.has_error === 'true' ||
        conversationState.has_error === true ||
        conversationState.has_error === 'true';

      if (hasError) {
        // Find the best error message available
        let errMsg = data.user_friendly_error ||
          conversationState.user_friendly_error ||
          data.message ||
          conversationState.message ||
          "I'm having trouble processing your request right now. Please try again.";

        // If data.response is a non-empty string, it might actually be a partial response or error
        if (data.response && typeof data.response === 'string' && data.response.length > 0) {
          errMsg = data.response;
        } else if (typeof data.response === 'object' && data.response !== null) {
          errMsg = data.response.text || data.response.message || errMsg;
        }

        throw new Error(errMsg);
      }

      // 2. Response Extraction: Try to find a valid text response
      if (typeof data === 'string') {
        return data;
      } else if (data && typeof data === 'object') {

        // Try standard response field
        if (data.response && typeof data.response === 'string' && data.response.trim().length > 0) {
          return data.response;
        }

        // Try other common field names (non-empty only)
        const possibleFields = ['message', 'text', 'answer', 'reply', 'result', 'data', 'output', 'content'];
        for (const field of possibleFields) {
          if (data[field] && typeof data[field] === 'string' && data[field].trim().length > 0) {
            return data[field];
          }
        }

        // If response is an object, try to extract text from it
        if (data.response && typeof data.response === 'object') {
          return data.response.text || data.response.message || data.response.content || JSON.stringify(data.response);
        }

        // Try to find ANY string field that might be the response
        for (const key in data) {
          if (typeof data[key] === 'string' && data[key].length > 20) {
            return data[key];
          }
        }

        // Last resort: return error message if present, otherwise stringify data
        return data.user_friendly_error || data.message || (Object.keys(data).length > 0 ? JSON.stringify(data) : "I received your message!");
      }

      return "I received your message!";

    } catch (error) {
      // Re-throw the specific error message to be caught by sendMessage
      throw error;
    }
  }

  /**
   * Send user message and get bot response
   */
  async function sendMessage(questionnaireAnswers = []) {
    const message = input.value.trim();

    // Prevent duplicate requests
    if (!message || isSending) {
      return;
    }

    isSending = true;
    const tokenAtStart = uiConversationToken;
    input.disabled = true;
    sendBtn.disabled = true;

    addMessage(message, true);
    input.value = "";
    input.style.height = '38px';

    showTypingIndicator();

    try {
      // Ensure answers is an array
      const currentAnswers = Array.isArray(questionnaireAnswers) ? questionnaireAnswers : [];
      const botResponse = await sendMessageToAPI(message, currentAnswers);
      hideTypingIndicator();

      // CRITICAL: If user switched product/guided options while we were waiting,
      // don't render the stale response into the current chat.
      if (tokenAtStart !== uiConversationToken) {
        return;
      }

      addMessage(botResponse, false);

      if (activeGuidedOptions && Array.isArray(activeGuidedOptions) && activeGuidedOptions.length > 0) {
        if (guidedRepliesTimeout) clearTimeout(guidedRepliesTimeout);
        // Consolidate re-rendering logic: use ensureGuidedRepliesVisible which handles all state checks.
        ensureGuidedRepliesVisible();
      } else {
        // Fallback: Try to recover from current product history if variable was lost.
        ensureGuidedRepliesVisible();
      }
    } catch (error) {
      hideTypingIndicator();
      if (tokenAtStart !== uiConversationToken) {
        return;
      }
      // Show the actual error message from the API or a fallback
      const errorMessage = error.message || "I'm having trouble processing your request right now. Please try again in a moment.";
      addMessage(errorMessage, false);
      
      // Ensure options are re-shown even after an error.
      ensureGuidedRepliesVisible();
    } finally {
      input.disabled = false;
      sendBtn.disabled = false;
      isSending = false;
      
      // Ensure visibility check after everything is settled.
      ensureGuidedRepliesVisible();
      
      if (input) {
        const inputContainer = document.getElementById('chatbot-input');
        const isVisible = !!(inputContainer && inputContainer.style.display !== 'none');
        if (isVisible) {
          focusInputSafely();
        }
      }
    }
  }

  /**
   * Add initial welcome message
   */
  function addWelcomeMessage(messageOrSmooth = true, deprecatedSmooth = true) {
    let text = "Welcome! I'm your skincare assistant. How can I help you today?";
    let shouldScroll = true;

    if (typeof messageOrSmooth === "string") {
      text = messageOrSmooth;
      shouldScroll = deprecatedSmooth;
    } else if (typeof messageOrSmooth === "boolean") {
      shouldScroll = messageOrSmooth;
    }

    addMessage(text, false, shouldScroll);
  }

  /**
   * Add quick reply buttons
   */
  function addQuickReplies(options, shouldScroll = true) {
    if (!options || !Array.isArray(options)) {
        console.warn("[ChatbotMessages] addQuickReplies called with invalid options:", options);
        return;
    }

    console.log("[ChatbotMessages] Rendering FAQ chips for options count:", options.length);

    // Replace any existing quick replies to avoid duplicates (history + persistence).
    if (messagesContainer) {
      const existing = messagesContainer.querySelectorAll('.quick-replies-wrapper, .quick-replies-container');
      existing.forEach(function (el) {
        try { el.remove(); } catch (e) { /* ignore */ }
      });
    }

    // Container for quick replies
    const messageWrapper = document.createElement("div");
    messageWrapper.className = "message bot-message quick-replies-wrapper";
    messageWrapper.style.display = "flex";
    messageWrapper.style.width = "100%";
    // No avatar here as per user request to avoid redundancy

    const repliesDiv = document.createElement("div");
    repliesDiv.className = "quick-replies-container";
    repliesDiv.style.display = "flex";
    repliesDiv.style.flexDirection = "row";
    repliesDiv.style.flexWrap = "wrap";
    repliesDiv.style.justifyContent = "flex-start";
    repliesDiv.style.alignItems = "flex-start";
    repliesDiv.style.alignSelf = "stretch";
    repliesDiv.style.width = "100%";
    repliesDiv.style.boxSizing = "border-box";
    repliesDiv.style.gap = "8px";
    repliesDiv.style.marginTop = "8px";
    repliesDiv.style.marginBottom = "12px";
    repliesDiv.style.marginLeft = "0"; // Guided FAQ: no left/right inset

    // Keep the full FAQ list visible at all times.
    // Asked questions remain visible but get disabled styling.
    const normalizedSeen = new Set();
    let finalOptions = options
      .map(function (opt) { return opt == null ? '' : String(opt).trim(); })
      .filter(function (opt) { return opt.length > 0; })
      .filter(function (opt) {
        const n = normalizeGuidedQuestion(opt);
        if (!n || normalizedSeen.has(n)) return false;
        normalizedSeen.add(n);
        return true;
      });
    if (!finalOptions.length) {
      finalOptions = ['My question is not listed above'];
    } else if (!normalizedSeen.has(CUSTOM_QUESTION_OPTION)) {
      finalOptions.push('My question is not listed above');
    }

    finalOptions.forEach(opt => {
      const btn = document.createElement("button");
      btn.className = "quick-reply-btn";
      btn.textContent = opt;

      const normalizedOpt = normalizeGuidedQuestion(opt);
      const isCustomQuestionBtn = normalizedOpt === CUSTOM_QUESTION_OPTION;
      const isAlreadyAsked = !isCustomQuestionBtn && askedQuestions.has(normalizedOpt);

      if (isCustomQuestionBtn) {
        btn.classList.add('custom-question-btn');
      } else if (isAlreadyAsked) {
        btn.classList.add('already-asked-btn');
      }

      btn.disabled = isAlreadyAsked;
      
      btn.onclick = function() {
          const val = this.textContent;
          console.log("[ChatbotMessages] FAQ Clicked:", val);
          sendMessage([], val);
      };
      
      repliesDiv.appendChild(btn);
    });

    if (messagesContainer) {
      messageWrapper.appendChild(repliesDiv);
      messagesContainer.appendChild(messageWrapper);
      console.log("[ChatbotMessages] FAQ Container appended to messagesContainer");
      if (shouldScroll) {
          setTimeout(() => scrollToBottom(true), 50);
      }
    } else {
        console.error("[ChatbotMessages] Cannot append FAQ: messagesContainer is missing");
    }
  }

  function ensureGuidedRepliesVisible() {
    if (!messagesContainer) return;
    
    if (!activeGuidedOptions || !Array.isArray(activeGuidedOptions) || activeGuidedOptions.length === 0) {
        // Recovery attempt: check if we have history for this product that includes options
        if (currentGuidedProductKey && productHistories[currentGuidedProductKey] && productHistories[currentGuidedProductKey].options) {
            activeGuidedOptions = productHistories[currentGuidedProductKey].options;
            console.log("[ChatbotMessages] Recovered activeGuidedOptions from history:", activeGuidedOptions.length);
        } else {
            console.log("[ChatbotMessages] No activeGuidedOptions available to render.");
            return;
        }
    }
    
    // Always re-render to keep asked-state/disabled-state consistent after each turn.
    if (guidedRepliesTimeout) clearTimeout(guidedRepliesTimeout);
    
    // Use a slightly longer delay to ensure the container height is updated after bot message
    guidedRepliesTimeout = setTimeout(() => {
        addQuickReplies(activeGuidedOptions, true);
        
        // Guided FAQ mode: Hide text input
        const inputContainer = document.getElementById('chatbot-input');
        if (inputContainer) {
          inputContainer.style.display = 'none';
        }
        
        // Ensure this state is saved
        persistCurrentProductSnapshot();
        
        // Secondary scroll check to make sure they are really in view
        setTimeout(() => scrollToBottom(true), 80);
    }, 150);
  }

  /**
   * Clear all messages
   */
  function clearMessages() {
    messagesContainer.innerHTML = '';
    if (messagesContainer) messagesContainer.scrollTop = 0;
  }

  /**
   * Save current chat HTML for a specific product
   */
  function saveHistory(productId) {
    if (!productId || !messagesContainer) return;
    persistAskedQuestionsForCurrentProduct();
    productHistories[productId] = {
      html: messagesContainer.innerHTML,
      asked: new Set(Array.from(askedQuestions).map(normalizeGuidedQuestion)),
      options: activeGuidedOptions // Persist the question list too!
    };
    askedQuestionsByProduct[productId] = new Set(Array.from(askedQuestions).map(normalizeGuidedQuestion));
    flushGuidedStateToStorage();
  }

  /**
   * Load previous chat HTML for a specific product
   */
  function loadHistory(productId) {
    if (!productId || !productHistories[productId]) return false;

    const history = productHistories[productId];
    messagesContainer.innerHTML = history.html;
    // Restore options if they exist in history
    if (history.options) {
        activeGuidedOptions = history.options;
    }
    // Backward-compatible: normalize old history entries saved before normalization.
    askedQuestions = new Set(Array.from(history.asked || []).map(normalizeGuidedQuestion));
    persistAskedQuestionsForCurrentProduct();

    requestAnimationFrame(() => {
      if (messagesContainer) messagesContainer.scrollTop = 0;
      ensureGuidedRepliesVisible();
    });
    return true;
  }

  loadGuidedStateFromStorage();

  function hasPersistedGuidedActivity() {
    if (Object.keys(productHistories).length) return true;
    for (var k in askedQuestionsByProduct) {
      if (!Object.prototype.hasOwnProperty.call(askedQuestionsByProduct, k)) continue;
      var s = askedQuestionsByProduct[k];
      if (s && typeof s.size === 'number' && s.size > 0) return true;
    }
    return false;
  }

  function clearPersistedGuidedState() {
    try { localStorage.removeItem(GUIDED_STATE_STORAGE_KEY); } catch (e) { /* ignore */ }
    try { localStorage.removeItem('codeskin_chatbot_last_guided_product_id'); } catch (e2) { /* ignore */ }
    productHistories = {};
    askedQuestionsByProduct = {};
    askedQuestions.clear();
    if (messagesContainer) {
      try { messagesContainer.innerHTML = ''; } catch (e2) { /* ignore */ }
    }
  }

  // Public API
  return {
    setDOMReferences: setDOMReferences,
    addMessage: addMessage,
    sendMessage: sendMessage,
    addWelcomeMessage: addWelcomeMessage,
    clearMessages: clearMessages,
    addQuickReplies: addQuickReplies,
    scrollToBottom: scrollToBottom,
    scrollToTop: scrollToTop,
    setGuidedOptions: function (options) {
      console.log("[ChatbotMessages] setGuidedOptions called with", (options ? options.length : 0), "questions");
      activeGuidedOptions = options;
      
      // Persist options immediately so they survive if turn processing takes time
      persistCurrentProductSnapshot();
      
      ensureGuidedRepliesVisible();
    },
    setCurrentProduct: function (product) {
      persistAskedQuestionsForCurrentProduct();
      if (!product) {
        currentProductContext = null;
        currentGuidedProductKey = null;
        askedQuestions.clear();
        bumpConversationToken();
        return;
      }
      // Use Shopify product ID (not variant). Prefer numeric IDs; strip GID if present.
      const rawId = (product.shopify_product_id || product.product_id || product.id || "").toString();
      const numericId = rawId.includes('/') ? rawId.split('/').pop() : rawId;
    // Keep API payload safe (numeric only), but use a stable cache id even when non-numeric.
    const cacheProductId = String(numericId).trim();
    const productId = /^\d+$/.test(cacheProductId) ? cacheProductId : "";
      const variantId = (product.variant_id || product.shopify_variant_id || "").toString().trim();
      currentProductContext = {
        product_id: productId,
        variant_id: variantId || null,
        product_name: product.product_name || product.product || product.title || product.name || null
      };
    currentGuidedProductKey = getGuidedProductKey(Object.assign({ cache_product_id: cacheProductId }, currentProductContext));
      const cachedAsked = askedQuestionsByProduct[currentGuidedProductKey];
      askedQuestions = new Set(Array.from(cachedAsked || []).map(normalizeGuidedQuestion));
      bumpConversationToken();
      ensureGuidedRepliesVisible();
    },
    clearGuidedOptions: function () {
      persistAskedQuestionsForCurrentProduct();
      activeGuidedOptions = null;
      currentProductContext = null;
      currentGuidedProductKey = null;
      askedQuestions.clear();
      cancelPendingResponse();
    },
    cancelPendingResponse: cancelPendingResponse,
    saveHistory: saveHistory,
    loadHistory: loadHistory,
    // Override FAQ asked-state key to match the exact prodId used by chatbot-main history.
    // This prevents FAQ disabled-state from leaking between products.
    setGuidedProductKey: setGuidedProductKey,
    hasPersistedGuidedActivity: hasPersistedGuidedActivity,
    clearPersistedGuidedState: clearPersistedGuidedState
  };
}

// Create global instance
const ChatbotMessages = initChatbotMessages();
